/* eslint-disable camelcase */
import React, { useState, useReducer, Fragment } from 'react';
import classNames from 'classnames';
import parse from 'html-react-parser';
import smoothscroll from 'smoothscroll-polyfill';

import VideoPlayer from 'nbc-station/client/src/components/nbc-jw-player';
import {
  metaPropHandler,
  VideoContentUnitProps,
  VideoProps,
} from 'nbc-station/client/src/components/nbc-jw-player/utils';

import AdUnit from 'nbc-station/client/src/components/AdUnit';

const {
  wp: {
    i18n: {
      __,
    },
  },
  nbc: {
    callLetters,
  },
} = window;

/**
 * Show archived videos.
 *
 * Show six on load and all when see all is clicked.
 *
 * @param {Object} state
 * @returns {Object} archiveVideos
 */
function setArchivedVideos(state) {
  const { archiveVideos, seeAll } = state;

  if (! seeAll) {
    return archiveVideos.slice(0, 5);
  }

  return archiveVideos;
}

/**
 * Render an ad unit.
 *
 * @param {String} adUnit Name of the ad unit to display.
 */
const renderAdUnit = (adUnit = 'topbox') => (
  <div className="ad moduleAd">
    <AdUnit name={adUnit} />
  </div>
);

/**
 * Render out a list of movies.
 *
 * The layout is different depending on the type.
 *
 * @param {String}  type            List item type.
 * @param {Object}  state           Current state.
 * @param {Function} setActiveVideo Sets the active video.
 * @returns {*}
 */
const renderRightListItems = (type = 'featured', state, setActiveVideo) => {
  const videos = 'featured' === type
    ? state.featuredVideos
    : setArchivedVideos(state);

  return videos.map((video) => {
    const {
      title,
      image: {
        source_url,
      },
      network_object_id,
      formatted_length,
    } = video;

    const episodeCSS = classNames({
      episode: true,
      playing: network_object_id === state.activeVideo.network_object_id,
    });

    /* eslint-disable */
    return (
      <div
        key={video.network_object_id}
        className={episodeCSS}
        onClick={() => setActiveVideo(video, type)}
        onTouchEnd={() => setActiveVideo(video, type)}
      >
        <meta itemProp="partOfTVSeries" content={title} />
        <img itemProp="thumbnailUrl" src={source_url} alt={title} />
        <h2 itemProp="name">
          {title}
          <span className="totalRunTime"> ({formatted_length})</span>
        </h2>
        <div className="playButtonTiny" />
      </div>
    );
    /* eslint-enable */
  });
};

/**
 * Set the active video.
 *
 * Adds support for passing in a video ID and have it be in the featured position.
 *
 * @param {Object} video
 * @param {Array}  featuredVideos
 * @returns {Object|Array}
 */
function setFeaturedVideo(video, featuredVideos) {
  const hasMeta = Object.prototype.hasOwnProperty.call(video, 'meta');

  if (hasMeta) {
    return video;
  }

  return featuredVideos[0] || [];
}

/**
 * State reducer.
 *
 * @param {Object} state
 * @param {Object} action
 * @returns {Object}
 */
function reducer(state, action) {
  if ('seeAll' === action.type) {
    return {
      ...state,
      seeAll: action.payload,
    };
  }

  return {
    ...state,
    activeVideo: action.payload,
  };
}

const VideoCarousel = (props) => {
  const initialState = {
    featuredVideos: props.featuredVideos,
    archiveVideos: props.archiveVideos,
    activeVideo: setFeaturedVideo(props.video, props.featuredVideos),
    seeAll: false,
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const [autoplay, setAutoPlay] = useState(false);

  const handleSetActiveVideo = (video, type) => {
    setAutoPlay(true);
    dispatch({ payload: video });

    if ('featured' !== type) {
      smoothscroll.polyfill();
      document
        .getElementById('bodyContainer')
        ?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSetSeeAll = (seeAll) => {
    dispatch({ type: 'seeAll', payload: seeAll });
  };

  const {
    activeVideo,
    featuredVideos,
    archiveVideos,
  } = state;

  const {
    accountId,
    brand,
    host,
    loadMoreText,
    station,
    twitter,
  } = props;

  const video = {
    ...metaPropHandler(activeVideo as VideoContentUnitProps),
  } as VideoProps;

  return 0 === featuredVideos.length && 0 === archiveVideos.length
    ? null
    : (
      <Fragment>
        <div id="playerRegion" className="opacityDark">
          <div id="coziPlayer" className="video-player">
            <VideoPlayer
              accountId={accountId}
              autoplay={autoplay}
              host={host}
              twitter={twitter}
              brand={brand}
              video={video}
              islead={false}
              callLetters={callLetters}
              station={station}
              hideShare={false}
            />
          </div>
          <h2 id="currentlyPlayingTitle">{activeVideo.title}</h2>
          <h3 id="currentlyPlayingSummary">
            {parse(activeVideo.summary)}
            <span className="totalRunTime">
              ({activeVideo.formatted_length})
            </span>
          </h3>
        </div>
        <div className="right-list">
          { renderRightListItems('featured', state, handleSetActiveVideo) }
        </div>
        <div className="episode-grid">
          { renderAdUnit() }
          { renderRightListItems('archive', state, handleSetActiveVideo) }
        </div>
        <div style={{ clear: 'both' }} />
        {
          ! state.seeAll && (
            <div className="see-all-container">
              <button
                onClick={() => handleSetSeeAll(true)}
                className="see-all"
                type="button"
              >
                {loadMoreText}
              </button>
            </div>
          )
        }
      </Fragment>
    );
};

VideoCarousel.defaultProps = {
  video: {},
  loadMoreText: __('See all', 'cozi'),
};

export default VideoCarousel;
